import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, TextField, Button, Paper, CircularProgress, Alert, Stack } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import API_ENDPOINTS from '../config/apiConfig';
import { useTranslation } from '../i18n/i18n';

function ProspectApply() {
  const { t } = useTranslation();
  const { token } = useParams();
  const [vacancy, setVacancy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [serverError, setServerError] = useState('');

  const [fullname, setFullname] = useState('');
  const [email, setEmail] = useState('');
  const [resumeFile, setResumeFile] = useState(null);
  const [coverLetter, setCoverLetter] = useState('');

  useEffect(() => {
    const fetchVacancy = async () => {
      try {
        const res = await fetch(API_ENDPOINTS.APPLY_GET(token));
        if (!res.ok) {
          setError(t('apply_error_invalid_token'));
          return;
        }
        const data = await res.json();
        setVacancy(data);
      } catch {
        setError(t('apply_error_invalid_token'));
      } finally {
        setLoading(false);
      }
    };
    fetchVacancy();
  }, [token, t]);

  const validate = () => {
    const errs = {};
    if (!fullname.trim() || fullname.trim().length < 2) errs.fullname = t('apply_error_name_required');
    if (!email.trim()) errs.email = t('apply_error_email_required');
    else if (!/^[\w.-]+@[\w.-]+\.\w+$/.test(email.trim())) errs.email = t('apply_error_email_invalid');
    if (!resumeFile) errs.resume = t('apply_error_resume_required');
    else {
      const ext = resumeFile.name.split('.').pop().toLowerCase();
      if (!['pdf', 'docx'].includes(ext)) errs.resume = t('apply_error_resume_type');
      else if (resumeFile.size > 10 * 1024 * 1024) errs.resume = t('apply_error_resume_size');
    }
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');
    const errs = validate();
    setFormErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('fullname', fullname.trim());
      formData.append('email', email.trim());
      formData.append('cover_letter', coverLetter.trim());
      formData.append('resume', resumeFile);

      const res = await fetch(API_ENDPOINTS.APPLY_SUBMIT(token), {
        method: 'POST',
        body: formData,
      });

      if (res.ok) {
        setSubmitted(true);
      } else {
        const data = await res.json();
        if (res.status === 409) {
          setServerError(t('apply_error_duplicate'));
        } else {
          setServerError(data.message || t('apply_error_invalid_token'));
        }
      }
    } catch {
      setServerError(t('apply_error_invalid_token'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 2 }}>
        <Paper sx={{ p: 4, maxWidth: 500, width: '100%', textAlign: 'center' }}>
          <Alert severity="error">{error}</Alert>
        </Paper>
      </Box>
    );
  }

  if (submitted) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 2 }}>
        <Paper sx={{ p: 4, maxWidth: 500, width: '100%', textAlign: 'center' }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>{t('apply_success_title')}</Typography>
          <Typography color="text.secondary">{t('apply_success_message')}</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f5f5', p: 2 }}>
      <Paper sx={{ p: 4, maxWidth: 600, width: '100%' }}>
        <Typography variant="h5" gutterBottom>{t('apply_title')}</Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>{vacancy?.title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{vacancy?.description}</Typography>

        {serverError && <Alert severity="error" sx={{ mb: 2 }}>{serverError}</Alert>}

        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              label={t('apply_fullname')}
              value={fullname}
              onChange={(e) => setFullname(e.target.value)}
              error={!!formErrors.fullname}
              helperText={formErrors.fullname}
              fullWidth
              required
            />
            <TextField
              label={t('apply_email')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!!formErrors.email}
              helperText={formErrors.email}
              fullWidth
              required
            />
            <Box>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
                fullWidth
                sx={{ textTransform: 'none' }}
              >
                {resumeFile ? resumeFile.name : t('apply_resume_upload')}
                <input
                  type="file"
                  hidden
                  accept=".pdf,.docx"
                  onChange={(e) => setResumeFile(e.target.files[0] || null)}
                />
              </Button>
              {formErrors.resume && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {formErrors.resume}
                </Typography>
              )}
            </Box>
            <TextField
              label={t('apply_cover_letter')}
              value={coverLetter}
              onChange={(e) => setCoverLetter(e.target.value)}
              fullWidth
              multiline
              minRows={4}
              placeholder={t('apply_cover_letter_placeholder')}
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} /> : null}
            >
              {submitting ? t('apply_uploading') : t('apply_submit')}
            </Button>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
}

export default ProspectApply;
